import {ChannelTypesEnum} from '../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {ConversationTrackerStatusesEnum} from '../../../../libs/shared/core/src/models/api/conversationTrackerModel';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {MessageModel} from '../../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {TeamModel} from '../../../../libs/shared/core/src/models/api/teamModel';
import {TicketStatusModel} from '../../../../libs/shared/core/src/models/api/ticketStatusModel';
import {CellsContext} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {AppParametricSelector, createSelector} from '../../../../srcCommon/helpers/reselectHelpers';
import {ConversationStatusesEnum, UnreadStatusesEnum} from '../../../helpers/models/conversationHelpers';
import {ActivityModel} from '../../../models/api/activityModel';
import {ApplicationChannelTypeModel} from '../../../models/api/applicationChannelTypeModel';
import {ConversationHighlightModel} from '../../../models/api/conversationHighlightModel';
import {ConversationBaseModel} from '../../../models/api/conversationModel';
import {VisualConversationSendersModel} from '../../../models/api/conversationSendersModel';
import {DraftSummaryModel} from '../../../models/api/draftSummaryModel';
import {ExternalChannelTypeModel} from '../../../models/api/externalChannelTypeModel';
import {TagModel} from '../../../models/api/tagModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {getCompanyPreferences} from '../../../store/data/company/companySelectors';
import {
  BumpCommentModel,
  CommentDraftSummary,
  LastCommentDraftModel,
  LastCommentModel,
} from '../../../store/data/conversations/conversationCellSelectors';
import {VisualSearchResultCommentSummary} from './content/cell/cellActivity';
import {CellOverlayProps} from './content/cell/cellOverlay';
import {VisualSearchActivityModel} from './content/cell/cellSearchActivitySummaryHighlight';

export interface ConversationCellCommonProps {
  applicationChannelType?: ApplicationChannelTypeModel;
  assignee?: TeammateModel;
  bumpActivity?: ActivityModel;
  bumpComment?: BumpCommentModel;
  bumpTeammateSource?: TeammateModel;
  cellsContext: CellsContext;
  commentDraftSummary?: CommentDraftSummary;
  commentingTeammate?: TeammateModel;
  conversation: ConversationBaseModel;
  date: number;
  draft?: DraftSummaryModel;
  draftSeedAuthors?: ReadonlyArray<TeammateModel>;
  externalChannelType?: ExternalChannelTypeModel;
  inbox?: InboxModel;
  isOriginalConversation?: boolean;
  isSelected?: boolean;
  isStarred?: boolean;
  lastComment?: LastCommentModel;
  lastCommentDraft?: LastCommentDraftModel;
  lastManualMessage: MessageModel | undefined;
  mentionableAliases?: ReadonlySet<string>;
  numMessages?: number;
  overlay?: CellOverlayProps;
  recipient?: VisualRecipientModel;
  reminderDate?: number;
  slaTimerDate?: number;
  replyingTeammate?: TeammateModel;
  shouldConsiderInPrivateTag?: boolean;
  shouldRenderConversationSenders?: boolean;
  status: ConversationStatusesEnum;
  ticketStatus: TicketStatusModel | undefined;
  channelTypes: ReadonlyArray<ChannelTypesEnum>;
  tags: ReadonlyArray<TagModel>;
  team?: TeamModel;
  trackerStatus?: ConversationTrackerStatusesEnum;
  trackerTeammates: ReadonlyArray<TeammateModel>;
  unreadStatus: UnreadStatusesEnum;
  userId?: number;

  // Search
  conversationHighlight?: ConversationHighlightModel;
  searchActivity?: VisualSearchActivityModel;
  searchResultCommentSummary?: VisualSearchResultCommentSummary;
  conversationSenders?: VisualConversationSendersModel;
}

export function makeGetShouldRenderConversationSenders<P>(
  getVisualConversationSenders: AppParametricSelector<P, VisualConversationSendersModel | undefined>,
  getIsSearch: AppParametricSelector<P, boolean | undefined>,
) {
  return createSelector(
    [getVisualConversationSenders, getCompanyPreferences, getIsSearch],
    (conversationSenders, preferences, isSearch): boolean => {
      const hasFirstSender =
        conversationSenders &&
        ('singleSender' in conversationSenders ||
          ('firstSender' in conversationSenders && conversationSenders.firstSender));

      return Boolean(hasFirstSender);
    },
  );
}
